import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';
import { CostosPanel } from './Costos';

function loadScript(src: string): Promise<void> {
  return new Promise((res, rej) => {
    if (Array.from(document.scripts).some(sc => sc.src === src)) return res();
    const sc = document.createElement('script');
    sc.src = src; sc.onload = () => res(); sc.onerror = () => rej(new Error('cdn'));
    document.head.appendChild(sc);
  });
}

async function exportPOExcel(poId: string, poNumber: string) {
  const { data: po } = await supabase.from('purchase_orders').select('*').eq('id', poId).maybeSingle();
  if (!po) return;
  const pod = po as any;
  const [{ data: sup }, { data: its }] = await Promise.all([
    pod.supplier_id
      ? supabase.from('suppliers').select('name, contact_name, email, phone, country, incoterms, payment_terms, lead_time_days').eq('id', pod.supplier_id).maybeSingle()
      : Promise.resolve({ data: null }),
    supabase.from('purchase_order_items').select('product_id, variant_id, sku, description, notes, quantity, unit_cost, line_total, sort_order').eq('po_id', poId).order('sort_order'),
  ]);
  const items = (its as any[]) ?? [];
  // Traer specs + foto de cada producto y atributos de variante
  const pids = Array.from(new Set(items.map(i => i.product_id).filter(Boolean)));
  const vids = Array.from(new Set(items.map(i => i.variant_id).filter(Boolean)));
  const [{ data: prods }, { data: vars }] = await Promise.all([
    pids.length ? supabase.from('products').select('id, sku, images, specs, custom_fields, product_type').in('id', pids) : Promise.resolve({ data: [] }),
    vids.length ? supabase.from('product_variants').select('id, full_sku, finish, color_temperature_k, beam_angle_deg, wattage_w, dim_method, attributes').in('id', vids) : Promise.resolve({ data: [] }),
  ]);
  const pMap: Record<string, any> = {}; for (const p of (prods as any[]) ?? []) pMap[p.id] = p;
  const vMap: Record<string, any> = {}; for (const v of (vars as any[]) ?? []) vMap[v.id] = v;
  const imgUrl = (p: any) => { const arr = Array.isArray(p?.images) ? p.images : []; const t = arr.find((x: any) => x?.is_thumbnail) ?? arr[0]; return t?.url ?? ''; };

  await loadScript('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js');
  const XLSX = (window as any).XLSX;
  const s = sup as any;
  const cur = pod.currency || 'USD';
  const aoa: any[][] = [
    ['ORDEN DE COMPRA', poNumber],
    ['Proveedor', s?.name ?? ''],
    ['Contacto', s?.contact_name ?? '', 'Email', s?.email ?? ''],
    ['Fecha orden', pod.ordered_at ?? '', 'ETA', pod.expected_at ?? ''],
    ['Incoterms', s?.incoterms ?? '', 'Términos', s?.payment_terms ?? ''],
    ['Moneda', cur],
    [],
    ['No.', 'SKU proveedor / producto', 'Descripción', 'Configuración exacta', 'Material / Acabado', 'Medidas', 'Voltaje', 'Potencia (W)', 'CCT (K)', 'CRI', 'IP', 'Ángulo (°)', 'Lúmenes', 'Foto (URL)', 'Cant', 'Costo unit', 'Total'],
  ];
  let n = 0;
  for (const it of items) {
    n++;
    const sp = (pMap[it.product_id]?.specs ?? {}) as any;
    const variant = vMap[it.variant_id] ?? null;
    const variante = buildCharacteristics(pMap[it.product_id], variant);
    aoa.push([
      n, it.sku, it.description ?? '', variante,
      sp.material ?? '', sp.dimeo ?? '', sp.voltaje ?? '', sp.potencia ?? '', sp.cct ?? '', sp.cri ?? '', sp.ip ?? '', sp.angulo ?? '', sp.lumenes ?? '',
      imgUrl(pMap[it.product_id]),
      Number(it.quantity), Number(it.unit_cost), Number(it.line_total ?? it.quantity * it.unit_cost),
    ]);
  }
  aoa.push([]);
  aoa.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'Subtotal', Number(pod.subtotal ?? 0)]);
  if (pod.shipping) aoa.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'Envío/flete', Number(pod.shipping)]);
  if (pod.tax) aoa.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'Impuestos/aduana', Number(pod.tax)]);
  aoa.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'TOTAL', Number(pod.total ?? 0)]);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 5 }, { wch: 22 }, { wch: 30 }, { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 40 }, { wch: 6 }, { wch: 12 }, { wch: 14 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'OC');
  XLSX.writeFile(wb, `${poNumber}.xlsx`);
}

const miniAction: React.CSSProperties = {
  border: `1px solid ${theme.border}`, background: 'transparent', color: theme.brand,
  padding: '4px 9px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
  cursor: 'pointer', textDecoration: 'none', fontFamily: theme.font.sans, borderRadius: 2,
};

type PO = {
  id: string;
  po_number: string;
  supplier_id: string | null;
  supplier_name?: string;
  currency: string;
  subtotal: number;
  total: number;
  status: string;
  ordered_at: string | null;
  expected_at: string | null;
  received_at: string | null;
  invoice_number: string | null;
  notes: string | null;
  created_at: string;
  items_count?: number;
  po_type: 'manual' | 'in_stock' | 'on_order' | 'mixed' | null;
  triggered_by_order_id: string | null;
  invoice_received_at: string | null;
  facturapi_invoice_id: string | null;
  auto_generated: boolean;
};

type Supplier = {
  id: string;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  country: string;
  currency: string;
  lead_time_days: number | null;
  brand_lines: string[] | null;
  active: boolean;
  min_order_qty: number | null;
  min_order_value: number | null;
  order_multiple: number | null;
  incoterms: string | null;
  payment_terms: string | null;
  import_factor_pct: number | null;
};

type Sub = 'orders' | 'suppliers' | 'costos';

export function AdminCompras() {
  const [sub, setSub] = useState<Sub>('orders');
  const [showNewPO, setShowNewPO] = useState(false);
  const [showNewSupplier, setShowNewSupplier] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<string | null>(null);
  const [editingPO, setEditingPO] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  return (
    <AdminContainer
      title="Compras"
      eyebrow="Órdenes a proveedor"
      action={
        sub === 'orders' ? (
          <button style={btnPrimary} onClick={() => setShowNewPO(true)}>
            + Nueva PO
          </button>
        ) : sub === 'suppliers' ? (
          <button style={btnPrimary} onClick={() => setShowNewSupplier(true)}>
            + Nuevo proveedor
          </button>
        ) : undefined
      }
    >
      <div style={{ display: 'inline-flex', background: theme.bg, border: `1px solid ${theme.border}`, marginBottom: 20 }}>
        <SubBtn active={sub === 'orders'} onClick={() => setSub('orders')}>
          📦 Órdenes de compra
        </SubBtn>
        <SubBtn active={sub === 'suppliers'} onClick={() => setSub('suppliers')}>
          🏭 Proveedores
        </SubBtn>
        <SubBtn active={sub === 'costos'} onClick={() => setSub('costos')}>
          🔒 Costos
        </SubBtn>
      </div>

      {sub === 'orders' && <Orders key={reload} onEdit={setEditingPO} />}
      {sub === 'suppliers' && <Suppliers key={reload} onEdit={setEditingSupplier} />}
      {sub === 'costos' && <CostosPanel key={reload} />}

      {showNewPO && (
        <POModal
          mode="create"
          onClose={() => setShowNewPO(false)}
          onSaved={() => { setShowNewPO(false); setReload(r => r + 1); }}
        />
      )}
      {editingPO && (
        <POModal
          mode="edit"
          poId={editingPO}
          onClose={() => setEditingPO(null)}
          onSaved={() => { setEditingPO(null); setReload(r => r + 1); }}
        />
      )}
      {showNewSupplier && (
        <SupplierModal
          onClose={() => setShowNewSupplier(false)}
          onSaved={() => { setShowNewSupplier(false); setReload(r => r + 1); }}
        />
      )}
      {editingSupplier && (
        <SupplierModal
          supplierId={editingSupplier}
          onClose={() => setEditingSupplier(null)}
          onSaved={() => { setEditingSupplier(null); setReload(r => r + 1); }}
        />
      )}
    </AdminContainer>
  );
}

// ============ Lista de POs ============
function Orders({ onEdit }: { onEdit: (id: string) => void }) {
  const [pos, setPos] = useState<PO[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'manual' | 'in_stock' | 'on_order'>('all');
  const [reloadKey, setReloadKey] = useState(0);
  const [receiveId, setReceiveId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await supabase.from('purchase_orders')
        .select(`
          id, po_number, supplier_id, currency, subtotal, total, status,
          ordered_at, expected_at, received_at, invoice_number, notes, created_at,
          po_type, triggered_by_order_id, invoice_received_at, facturapi_invoice_id, auto_generated,
          supplier:suppliers ( name )
        `)
        .order('created_at', { ascending: false })
        .limit(500);
      const rows = ((data as any[]) ?? []).map(r => ({
        ...r,
        supplier_name: r.supplier?.name ?? null,
      })) as PO[];
      setPos(rows);
      setLoading(false);
    })();
  }, [reloadKey]);

  const filteredPos = pos.filter(p => filter === 'all' ? true : p.po_type === filter);

  async function markInvoiceReceived(po: PO) {
    const invoiceNumber = prompt(
      `Marcar factura recibida para ${po.po_number}.\nNúmero de factura del proveedor:`,
      po.invoice_number ?? ''
    );
    if (invoiceNumber === null) return;
    const { error } = await supabase.from('purchase_orders')
      .update({
        invoice_received_at: new Date().toISOString(),
        invoice_number: invoiceNumber || null,
        status: po.status === 'draft' || po.status === 'sent' ? 'received' : po.status,
      })
      .eq('id', po.id);
    if (error) { alert(error.message); return; }
    setReloadKey(k => k + 1);
  }

  if (loading) return <Loading />;

  if (pos.length === 0) {
    return (
      <div style={{ padding: 48, textAlign: 'center', background: theme.bg, border: `1px solid ${theme.border}` }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>📦</div>
        <div style={{ fontFamily: theme.font.head, fontSize: 18, color: theme.brand, marginBottom: 6 }}>
          Sin órdenes de compra aún
        </div>
        <div style={{ fontSize: 13, color: theme.textMuted }}>
          Crea tu primer PO para empezar a llevar control de recepciones y costos.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Filtro por tipo */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <TypePill active={filter === 'all'} onClick={() => setFilter('all')} count={pos.length}>
          Todas
        </TypePill>
        <TypePill active={filter === 'manual'} onClick={() => setFilter('manual')}
          count={pos.filter(p => p.po_type === 'manual').length}>
          Manual
        </TypePill>
        <TypePill active={filter === 'on_order'} onClick={() => setFilter('on_order')}
          count={pos.filter(p => p.po_type === 'on_order').length}>
          Del faltante de pedidos
        </TypePill>
      </div>

      <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 1050 }}>
          <thead>
            <tr style={{ background: theme.bgSoft }}>
              <Th>PO</Th>
              <Th>Proveedor</Th>
              <Th>Tipo</Th>
              <Th>Origen</Th>
              <Th>ETA</Th>
              <Th style={{ textAlign: 'right' }}>Total</Th>
              <Th>Status</Th>
              <Th>Factura</Th>
              <Th style={{ width: 90 }}> </Th>
            </tr>
          </thead>
          <tbody>
            {filteredPos.map(p => (
              <tr key={p.id} style={{ borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}
                  onClick={() => onEdit(p.id)}>
                <Td>
                  <span style={{ fontFamily: theme.font.mono, fontSize: 11, color: theme.brand, fontWeight: 600 }}>
                    {p.po_number}
                  </span>
                </Td>
                <Td>{p.supplier_name ?? '—'}</Td>
                <Td>
                  <PoTypeBadge type={p.po_type} auto={p.auto_generated} />
                </Td>
                <Td style={{ fontSize: 11, color: theme.textMuted }}>
                  {p.triggered_by_order_id ? (
                    <span style={{ fontFamily: theme.font.mono }}>
                      VTA-{p.triggered_by_order_id.slice(0, 6)}
                    </span>
                  ) : '—'}
                </Td>
                <Td>{p.expected_at ? new Date(p.expected_at).toLocaleDateString('es-MX') : '—'}</Td>
                <Td style={{ textAlign: 'right', fontWeight: 600 }}>
                  ${Number(p.total).toLocaleString('en-US', { maximumFractionDigits: 2 })} {p.currency}
                </Td>
                <Td>
                  <Tag color={poStatusColor(p.status)}>{p.status}</Tag>
                </Td>
                <Td style={{ fontSize: 11 }}>
                  {p.invoice_received_at ? (
                    <div style={{ color: '#15803D' }}>
                      ✓ {new Date(p.invoice_received_at).toLocaleDateString('es-MX')}
                      {p.invoice_number && <div style={{ color: theme.textMuted, fontFamily: theme.font.mono, fontSize: 10 }}>{p.invoice_number}</div>}
                    </div>
                  ) : (
                    <span style={{ color: theme.textMuted }}>Pendiente</span>
                  )}
                </Td>
                <Td onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <a href={`/compra/${p.id}/pdf`} target="_blank" rel="noopener noreferrer" style={miniAction}>PDF</a>
                    <button onClick={() => exportPOExcel(p.id, p.po_number)} style={miniAction}>Excel</button>
                    <button onClick={() => setReceiveId(p.id)} style={miniAction}>Recibir</button>
                    {!p.invoice_received_at && (
                      <button onClick={() => markInvoiceReceived(p)} style={{
                        background: theme.accent, color: theme.brand, border: 'none',
                        padding: '5px 10px', fontSize: 10, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 0.4, cursor: 'pointer',
                        fontFamily: theme.font.sans,
                      }}>
                        Factura
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredPos.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>
            No hay órdenes en este filtro.
          </div>
        )}
      </div>
      {receiveId && (
        <ReceptionModal poId={receiveId} onClose={() => setReceiveId(null)} onSaved={() => setReloadKey(r => r + 1)} />
      )}
    </div>
  );
}

// =====================================================================
// RECEPCIÓN — envíos del proveedor (packing list + tracking) + cotejo a inventario
// =====================================================================
function ReceptionModal({ poId, onClose, onSaved }: { poId: string; onClose: () => void; onSaved: () => void }) {
  const [ponum, setPonum] = useState('');
  const [ships, setShips] = useState<any[]>([]);
  const [whs, setWhs] = useState<{ code: string; name: string }[]>([]);
  const [carrier, setCarrier] = useState('');
  const [tracking, setTracking] = useState('');
  const [eta, setEta] = useState('');
  const [loc, setLoc] = useState('');
  const [recv, setRecv] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    const [{ data: po }, { data: sh }, { data: w }] = await Promise.all([
      supabase.from('purchase_orders').select('po_number').eq('id', poId).maybeSingle(),
      supabase.from('supplier_shipments').select('id, carrier, tracking, eta, status, received_at, supplier_shipment_items(id, sku, expected_qty, received_qty)').eq('po_id', poId).order('created_at', { ascending: false }),
      supabase.from('warehouses').select('code, name').eq('active', true).order('sort_order'),
    ]);
    setPonum((po as any)?.po_number ?? '');
    setShips((sh as any[]) ?? []);
    const wl = ((w as any[]) ?? []).map((x: any) => ({ code: x.code, name: x.name }));
    setWhs(wl);
    setLoc(prev => prev || (wl[0]?.code ?? 'oficina'));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [poId]);

  async function crearEnvio() {
    setBusy(true); setErr(null);
    const { error } = await supabase.rpc('create_shipment_from_po', { p_po_id: poId, p_carrier: carrier || null, p_tracking: tracking || null, p_eta: eta || null });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    setCarrier(''); setTracking(''); setEta(''); load();
  }
  async function recibir(shipId: string, items: any[]) {
    const lines = items.map(it => ({ shipment_item_id: it.id, received_qty: Number(recv[it.id] || 0) })).filter(l => l.received_qty > 0);
    if (lines.length === 0) { setErr('Captura cantidades recibidas.'); return; }
    if (!loc) { setErr('Elige bodega destino.'); return; }
    setBusy(true); setErr(null);
    const { error } = await supabase.rpc('receive_shipment', { p_shipment_id: shipId, p_lines: lines, p_location: loc });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    setRecv({}); await load(); onSaved();
  }

  const inp: React.CSSProperties = { padding: '8px 10px', border: `1px solid ${theme.border}`, fontSize: 13, color: theme.text, fontFamily: theme.font.sans, background: theme.bg, outline: 'none' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, width: '100%', maxWidth: 780, maxHeight: '94vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <button type="button" onClick={onClose} aria-label="Cerrar" style={{ position: 'absolute', top: 12, right: 16, background: 'transparent', border: 'none', color: theme.textMuted, fontSize: 26, lineHeight: 1, cursor: 'pointer', padding: 4, zIndex: 5 }}>×</button>
        <div style={{ padding: '20px 28px', borderBottom: `1px solid ${theme.border}` }}>
          <div style={{ fontSize: 11, color: theme.accentDark, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>Recepción · envíos del proveedor</div>
          <h2 style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 400, color: theme.brand, margin: '4px 0 0' }}>OC {ponum}</h2>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {/* Nuevo envío (packing list) */}
          <div style={{ border: `1px solid ${theme.border}`, padding: 14, marginBottom: 18 }}>
            <div style={{ fontWeight: 700, color: theme.brand, marginBottom: 8, fontSize: 13 }}>Registrar envío (packing list)</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input value={carrier} onChange={e => setCarrier(e.target.value)} placeholder="Fletera (FedEx, DHL…)" style={{ ...inp, width: 170 }} />
              <input value={tracking} onChange={e => setTracking(e.target.value)} placeholder="N° de guía / tracking" style={{ ...inp, width: 190 }} />
              <input type="date" value={eta} onChange={e => setEta(e.target.value)} title="Fecha estimada de arribo" style={{ ...inp, width: 150 }} />
              <button onClick={crearEnvio} disabled={busy} style={{ background: theme.brand, color: theme.textOnBrand, border: 'none', padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans }}>+ Envío</button>
            </div>
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 6 }}>Crea un envío con los renglones pendientes de la OC. Luego cotejas lo que físicamente llegó.</div>
          </div>

          {/* Bodega destino */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: theme.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Bodega destino</span>
            <select value={loc} onChange={e => setLoc(e.target.value)} style={{ ...inp }}>
              {whs.map(w => <option key={w.code} value={w.code}>{w.name}</option>)}
            </select>
          </div>

          {ships.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>Aún no hay envíos. Registra uno arriba.</div>}
          {ships.map(sh => (
            <div key={sh.id} style={{ border: `1px solid ${theme.border}`, marginBottom: 14 }}>
              <div style={{ padding: '10px 14px', background: theme.bgSoft, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, color: theme.brand }}>
                  <b>{sh.carrier || 'Envío'}</b>{sh.tracking ? ` · guía ${sh.tracking}` : ''}{sh.eta ? ` · ETA ${new Date(sh.eta + 'T00:00:00').toLocaleDateString('es-MX')}` : ''}
                </div>
                <Tag color={sh.status === 'received' ? '#15803D' : sh.status === 'partial' ? '#9A7B00' : theme.textMuted}>{sh.status}</Tag>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ background: theme.bg }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 10, color: theme.textMuted, textTransform: 'uppercase' }}>SKU</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 10, color: theme.textMuted }}>Esperado</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 10, color: theme.textMuted }}>Recibido</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 10, color: theme.textMuted }}>Recibir ahora</th>
                </tr></thead>
                <tbody>
                  {(sh.supplier_shipment_items ?? []).map((it: any) => {
                    const pend = Number(it.expected_qty) - Number(it.received_qty);
                    return (
                      <tr key={it.id} style={{ borderTop: `1px solid ${theme.border}` }}>
                        <td style={{ padding: '8px 12px', fontFamily: theme.font.mono, color: theme.brand }}>{it.sku}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{it.expected_qty}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: theme.textMuted }}>{it.received_qty}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                          {pend > 0
                            ? <input type="number" min={0} value={recv[it.id] ?? ''} onChange={e => setRecv(r => ({ ...r, [it.id]: e.target.value }))} style={{ ...inp, width: 80, textAlign: 'right' }} placeholder={String(pend)} />
                            : <span style={{ color: '#15803D' }}>✓</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {sh.status !== 'received' && (
                <div style={{ padding: '10px 14px', textAlign: 'right' }}>
                  <button onClick={() => recibir(sh.id, sh.supplier_shipment_items ?? [])} disabled={busy}
                    style={{ background: theme.accent, color: '#0e0e0e', border: 'none', padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans }}>
                    {busy ? 'Recibiendo…' : `Recibir a ${whs.find(w => w.code === loc)?.name ?? loc}`}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {err && <div style={{ padding: '8px 28px', background: 'rgba(220,38,38,0.07)', borderTop: `1px solid ${theme.err}`, color: theme.err, fontSize: 13 }}>{err}</div>}
        <div style={{ padding: '14px 28px', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans }}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

function TypePill({ active, onClick, count, children }: { active: boolean; onClick: () => void; count: number; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: active ? theme.brand : theme.bg,
      color: active ? theme.textOnBrand : theme.brand,
      border: `1px solid ${active ? theme.brand : theme.border}`,
      padding: '6px 14px', fontSize: 12, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: 0.4, cursor: 'pointer',
      fontFamily: theme.font.sans,
    }}>
      {children}
      <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.75, fontWeight: 500 }}>({count})</span>
    </button>
  );
}

function PoTypeBadge({ type, auto }: { type: string | null; auto: boolean }) {
  if (!type || type === 'manual') {
    return <Tag color={theme.textMuted}>{auto ? 'auto' : 'manual'}</Tag>;
  }
  if (type === 'in_stock') return <Tag color="#15803D">📦 En stock</Tag>;
  if (type === 'on_order') return <Tag color="#9A7B00">🕐 Bajo pedido</Tag>;
  if (type === 'mixed')    return <Tag color="#1E40AF">⚡ Mixta</Tag>;
  return <Tag color={theme.textMuted}>{type}</Tag>;
}

function poStatusColor(s: string): string {
  if (s === 'received') return '#15803D';
  if (s === 'partial') return '#9A7B00';
  if (s === 'sent' || s === 'confirmed') return '#1E40AF';
  if (s === 'cancelled') return '#B91C1C';
  return theme.textMuted;
}

// ============ Lista proveedores ============
function Suppliers({ onEdit }: { onEdit: (id: string) => void }) {
  const [items, setItems] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await supabase.from('suppliers')
        .select('id, name, contact_name, email, phone, country, currency, lead_time_days, brand_lines, active')
        .order('name');
      setItems((data as Supplier[]) ?? []);
      setLoading(false);
    })();
  }, []);

  if (loading) return <Loading />;

  if (items.length === 0) {
    return (
      <div style={{ padding: 48, textAlign: 'center', background: theme.bg, border: `1px solid ${theme.border}` }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🏭</div>
        <div style={{ fontFamily: theme.font.head, fontSize: 18, color: theme.brand, marginBottom: 6 }}>
          Sin proveedores configurados
        </div>
        <div style={{ fontSize: 13, color: theme.textMuted }}>
          Crea proveedores (Lutron, fábricas NULED, etc.) para asignarlos a tus POs.
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 800 }}>
        <thead>
          <tr style={{ background: theme.bgSoft }}>
            <Th>Nombre</Th>
            <Th>Contacto</Th>
            <Th>País</Th>
            <Th>Moneda</Th>
            <Th>Lead time</Th>
            <Th style={{ textAlign: 'right' }}>% Import</Th>
            <Th style={{ textAlign: 'right' }}>MOQ</Th>
            <Th style={{ textAlign: 'right' }}>Mín. orden</Th>
            <Th>Líneas</Th>
            <Th>Status</Th>
            <Th>{''}</Th>
          </tr>
        </thead>
        <tbody>
          {items.map(s => (
            <tr key={s.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
              <Td><strong style={{ color: theme.brand }}>{s.name}</strong></Td>
              <Td>
                {s.contact_name && <div style={{ fontSize: 12 }}>{s.contact_name}</div>}
                {s.email && <div style={{ fontSize: 11, color: theme.textMuted }}>{s.email}</div>}
                {s.phone && <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: theme.font.mono }}>{s.phone}</div>}
              </Td>
              <Td>{s.country}</Td>
              <Td>{s.currency}</Td>
              <Td>{s.lead_time_days ? `${s.lead_time_days} días` : '—'}</Td>
              <Td style={{ textAlign: 'right' }}>{s.import_factor_pct != null ? `${s.import_factor_pct}%` : '—'}</Td>
              <Td style={{ textAlign: 'right' }}>{s.min_order_qty ?? '—'}</Td>
              <Td style={{ textAlign: 'right' }}>{s.min_order_value != null ? `$${Number(s.min_order_value).toLocaleString('en-US')} ${s.currency}` : '—'}</Td>
              <Td>
                {(s.brand_lines ?? []).length > 0 ? (
                  <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {s.brand_lines!.map(b => (
                      <span key={b} style={{
                        fontSize: 9, fontWeight: 700, padding: '2px 5px',
                        background: theme.bgFooter, color: theme.brand, textTransform: 'uppercase',
                      }}>{b}</span>
                    ))}
                  </div>
                ) : '—'}
              </Td>
              <Td>
                <Tag color={s.active ? '#15803D' : theme.textMuted}>{s.active ? 'activo' : 'inactivo'}</Tag>
              </Td>
              <Td>
                <button onClick={() => onEdit(s.id)} style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans }}>Editar</button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============ Modal: Crear/editar PO ============
type POItem = {
  id?: string;
  product_id: string | null;
  variant_id: string | null;
  sku: string;
  description: string;
  characteristics: string;
  quantity: number;
  qty_received?: number;
  unit_cost: number;
};

// Arma la línea de características para que el proveedor fabrique correcto.
function buildCharacteristics(p: any, v: any | null): string {
  if (p?.product_type === 'compose_module' || p?.product_type === 'compose_base') {
    const attrs = (v?.attributes ?? {}) as Record<string, unknown>;
    const parts = [
      v?.full_sku ? `Código NULED: ${v.full_sku}` : null,
      attrs.supplier_line_type === 'base' ? 'Tipo: Base de montaje' : 'Tipo: Módulo de luz',
      attrs.scale_mm ? `Escala: Ø${attrs.scale_mm} mm` : null,
      attrs.module_count ? `Módulos: ${attrs.module_count}` : null,
      attrs.family ? `Familia: ${attrs.family}` : null,
      v?.wattage_w ? `Potencia: ${v.wattage_w}W` : attrs.potencia ? `Potencia: ${attrs.potencia}` : null,
      v?.color_temperature_k ? `CCT: ${v.color_temperature_k}K` : null,
      v?.beam_angle_deg ? `Haz: ${v.beam_angle_deg}°` : attrs.haz ? `Haz: ${attrs.haz}` : null,
      v?.finish ? `Acabado: ${v.finish}` : attrs.acabado ? `Acabado: ${attrs.acabado}` : null,
      v?.dim_method ? `Control: ${v.dim_method}` : null,
      attrs.shape ? `Forma: ${attrs.shape === 'round' ? 'Redonda' : 'Cuadrada'}` : null,
      attrs.mounting ? `Montaje: ${attrs.mounting === 'trim' ? 'Con trim' : 'Trimless'}` : null,
      attrs.dimensiones ? `Dimensiones: ${attrs.dimensiones}` : null,
      attrs.corte_plafon ? `Corte: ${attrs.corte_plafon}` : null,
    ];
    return parts.filter(Boolean).join(' · ');
  }
  const parts: string[] = [];
  if (v) {
    if (v.finish) parts.push(`Acabado: ${v.finish}`);
    if (v.color_temperature_k) parts.push(`${v.color_temperature_k}K`);
    if (v.beam_angle_deg) parts.push(`${v.beam_angle_deg}°`);
    if (v.wattage_w) parts.push(`${v.wattage_w}W`);
    if (v.voltage_v) parts.push(`${v.voltage_v}V`);
    if (v.dim_method) parts.push(String(v.dim_method));
    if (v.ip_rating) parts.push(`IP${v.ip_rating}`);
    for (const [k, val] of Object.entries((v.attributes ?? {}) as Record<string, unknown>)) {
      if (val != null && String(val).trim() !== '') parts.push(`${k}: ${val}`);
    }
  }
  const specs = (p?.specs ?? {}) as Record<string, unknown>;
  const has = (rx: RegExp) => parts.some(x => rx.test(x));
  const grab = (pred: (k: string) => boolean, label: string, skipRx?: RegExp) => {
    if (skipRx && has(skipRx)) return;
    for (const k of Object.keys(specs)) {
      if (pred(k.toLowerCase())) { const val = specs[k]; if (val != null && String(val).trim() !== '') { parts.push(label ? `${label}: ${val}` : String(val)); return; } }
    }
  };
  grab(k => k.includes('potenc'), '', /w$/i);
  grab(k => k.includes('voltaj'), '', /v$/i);
  grab(k => k === 'cct' || (k.includes('temperatura') && k.includes('color')), '', /k$/i);
  grab(k => k.includes('angul') || k.includes('apertur'), '', /°/);
  grab(k => (k.includes('ip') && k.length < 6) || k.includes('protecc'), '', /ip/i);
  grab(k => k === 'cri' || k.includes('reproduc'), 'CRI');
  grab(k => k.includes('lumen'), 'Flujo');
  grab(k => k.includes('material'), 'Material');
  grab(k => k.includes('dimension') || k.includes('medida'), 'Dim.');
  grab(k => k.includes('vida'), 'Vida útil');
  return parts.join(' · ');
}

function POModal({ mode, poId, onClose, onSaved }: {
  mode: 'create' | 'edit';
  poId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [poNumber, setPoNumber] = useState('');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierId, setSupplierId] = useState<string>('');
  const [currency, setCurrency] = useState('USD');
  const [orderedAt, setOrderedAt] = useState<string>('');
  const [expectedAt, setExpectedAt] = useState<string>('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('draft');
  const [shipping, setShipping] = useState(0);
  const [tax, setTax] = useState(0);
  const [items, setItems] = useState<POItem[]>([]);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [skuQuery, setSkuQuery] = useState('');
  const [skuMatches, setSkuMatches] = useState<{ product_id: string; variant_id: string | null; sku: string; name: string; characteristics: string }[]>([]);
  const [refCosts, setRefCosts] = useState<Record<string, { cost: number; cur: string; at: string | null }>>({});

  useEffect(() => {
    (async () => {
      const [supRes, numRes, poRes, itemsRes] = await Promise.all([
        supabase.from('suppliers').select('id, name, contact_name, email, phone, country, currency, lead_time_days, brand_lines, active, min_order_qty, min_order_value, order_multiple, incoterms, payment_terms').eq('active', true).order('name'),
        mode === 'create' ? supabase.rpc('next_po_number') : Promise.resolve({ data: null }),
        mode === 'edit' && poId
          ? supabase.from('purchase_orders').select('*').eq('id', poId).maybeSingle()
          : Promise.resolve({ data: null }),
        mode === 'edit' && poId
          ? supabase.from('purchase_order_items').select('*').eq('po_id', poId).order('sort_order')
          : Promise.resolve({ data: [] }),
      ]);
      setSuppliers(((supRes as any).data as Supplier[]) ?? []);
      if (mode === 'create') {
        setPoNumber((numRes as any).data ?? '');
        setOrderedAt(new Date().toISOString().slice(0, 10));
      }
      if (mode === 'edit') {
        const po = (poRes as any).data;
        if (po) {
          setPoNumber(po.po_number);
          setSupplierId(po.supplier_id ?? '');
          setCurrency(po.currency);
          setOrderedAt(po.ordered_at ?? '');
          setExpectedAt(po.expected_at ?? '');
          setInvoiceNumber(po.invoice_number ?? '');
          setNotes(po.notes ?? '');
          setStatus(po.status);
          setShipping(Number(po.shipping ?? 0));
          setTax(Number(po.tax ?? 0));
        }
        const its = ((itemsRes as any).data as any[]) ?? [];
        setItems(its.map(i => ({
          id: i.id,
          product_id: i.product_id,
          variant_id: i.variant_id ?? null,
          sku: i.sku,
          description: i.description ?? '',
          characteristics: i.notes ?? '',
          quantity: Number(i.quantity),
          qty_received: Number(i.qty_received ?? 0),
          unit_cost: Number(i.unit_cost),
        })));
      }
      setLoading(false);
    })();
  }, [mode, poId]);

  // Búsqueda de SKU/producto → una opción por VARIANTE (se compra por variante) con características.
  useEffect(() => {
    if (!skuQuery.trim() || skuQuery.length < 2) { setSkuMatches([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      const { data: prods } = await supabase.from('products')
        .select('id, sku, name, specs, custom_fields, product_type')
        .or(`sku.ilike.%${skuQuery}%,name.ilike.%${skuQuery}%`)
        .limit(12);
      const plist = (prods as any[]) ?? [];
      const ids = plist.map(p => p.id);
      let vlist: any[] = [];
      if (ids.length > 0) {
        const { data: vs } = await supabase.from('product_variants')
          .select('id, product_id, full_sku, sku_suffix, finish, color_temperature_k, beam_angle_deg, wattage_w, voltage_v, dim_method, ip_rating, attributes')
          .in('product_id', ids).eq('active', true).limit(300);
        vlist = (vs as any[]) ?? [];
      }
      const byP: Record<string, any[]> = {};
      for (const v of vlist) (byP[v.product_id] ||= []).push(v);
      const opts: { product_id: string; variant_id: string | null; sku: string; name: string; characteristics: string }[] = [];
      for (const p of plist) {
        const vs = byP[p.id] ?? [];
        if (vs.length === 0) {
          opts.push({ product_id: p.id, variant_id: null, sku: p.sku, name: p.name, characteristics: buildCharacteristics(p, null) });
        } else {
          for (const v of vs) {
            const isCompose = p.product_type === 'compose_module' || p.product_type === 'compose_base';
            const supplierSku = v.attributes?.supplier_sku ?? p.custom_fields?.supplier_sku;
            opts.push({
              product_id: p.id,
              variant_id: v.id,
              sku: isCompose && supplierSku ? String(supplierSku) : (v.full_sku ?? v.sku_suffix ?? p.sku),
              name: p.name,
              characteristics: buildCharacteristics(p, v),
            });
          }
        }
      }
      if (alive) setSkuMatches(opts.slice(0, 25));
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [skuQuery]);

  useEffect(() => {
    const ids = Array.from(new Set(items.map(i => i.product_id).filter(Boolean))) as string[];
    if (ids.length === 0) { setRefCosts({}); return; }
    let alive = true;
    supabase.from('product_costs')
      .select('product_id, last_purchase_cost, last_purchase_currency, last_purchase_at')
      .is('variant_id', null).in('product_id', ids)
      .then(({ data }) => {
        if (!alive) return;
        const m: Record<string, { cost: number; cur: string; at: string | null }> = {};
        for (const c of (data as any[]) ?? []) if (c.last_purchase_cost != null) m[c.product_id] = { cost: Number(c.last_purchase_cost), cur: c.last_purchase_currency ?? '', at: c.last_purchase_at };
        setRefCosts(m);
      });
    return () => { alive = false; };
  }, [items.map(i => i.product_id).join(',')]);

  const addItem = async (m: { product_id: string; variant_id: string | null; sku: string; name: string; characteristics: string }) => {
    let cost = 0;
    const { data } = await supabase.from('product_costs')
      .select('supplier_cost, variant_id')
      .eq('product_id', m.product_id);
    const rows = (data as any[]) ?? [];
    const exact = rows.find(r => r.variant_id === m.variant_id);
    const prodLevel = rows.find(r => r.variant_id === null);
    cost = Number((exact ?? prodLevel)?.supplier_cost ?? 0);
    setItems(prev => [...prev, {
      product_id: m.product_id, variant_id: m.variant_id, sku: m.sku,
      description: m.name, characteristics: m.characteristics,
      quantity: 1, unit_cost: cost,
    }]);
    setSkuQuery(''); setSkuMatches([]);
  };

  const updateItem = (idx: number, patch: Partial<POItem>) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };

  const removeItem = (idx: number) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const subtotal = items.reduce((s, i) => s + i.quantity * i.unit_cost, 0);
  const total = subtotal + shipping + tax;
  const selectedSupplier = suppliers.find(x => x.id === supplierId) || null;
  const totalQty = items.reduce((n, it) => n + Number(it.quantity || 0), 0);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (items.length === 0) { setErr('Agrega al menos un producto'); return; }
    if (selectedSupplier) {
      const below: string[] = [];
      if (selectedSupplier.min_order_qty && totalQty < selectedSupplier.min_order_qty) below.push(`cantidad ${totalQty} < MOQ ${selectedSupplier.min_order_qty}`);
      if (selectedSupplier.min_order_value && subtotal < selectedSupplier.min_order_value) below.push(`subtotal ${subtotal.toFixed(2)} < mínimo ${selectedSupplier.min_order_value} ${currency}`);
      if (below.length && !window.confirm(`Esta orden no alcanza el mínimo del proveedor (${below.join('; ')}). ¿Guardar de todas formas?`)) return;
    }
    setSaving(true); setErr(null);
    try {
      const poPayload = {
        po_number: poNumber,
        supplier_id: supplierId || null,
        currency,
        subtotal,
        shipping,
        tax,
        total,
        status,
        ordered_at: orderedAt || null,
        expected_at: expectedAt || null,
        invoice_number: invoiceNumber || null,
        notes: notes || null,
      };

      let savedPOId: string;
      if (mode === 'create') {
        const { data, error } = await supabase.from('purchase_orders').insert(poPayload).select('id').single();
        if (error) throw error;
        savedPOId = (data as any).id;
      } else {
        const { error } = await supabase.from('purchase_orders').update(poPayload).eq('id', poId!);
        if (error) throw error;
        savedPOId = poId!;
        // Borrar items existentes
        await supabase.from('purchase_order_items').delete().eq('po_id', savedPOId);
      }

      const itemsPayload = items.map((it, i) => ({
        po_id: savedPOId,
        product_id: it.product_id,
        variant_id: it.variant_id,
        sku: it.sku,
        description: it.description,
        notes: it.characteristics,
        quantity: it.quantity,
        qty_received: it.qty_received ?? 0,
        unit_cost: it.unit_cost,
        sort_order: i + 1,
      }));
      const { error: errItems } = await supabase.from('purchase_order_items').insert(itemsPayload);
      if (errItems) throw errItems;

      onSaved();
    } catch (e: any) {
      setErr(e.message ?? 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  async function receiveAll() {
    if (!poId || mode !== 'edit') return;
    if (!confirm('¿Marcar todo el PO como recibido? Esto generará movimientos de inventario.')) return;
    const recv = items.filter(it => it.id).map(it => ({
      po_item_id: it.id,
      qty_received: it.quantity - (it.qty_received ?? 0),
    }));
    const { error } = await supabase.rpc('receive_po_items', {
      p_po_id: poId,
      p_items: recv,
    });
    if (error) { alert(`Error: ${error.message}`); return; }
    onSaved();
  }

  if (loading) return <ModalShell onClose={onClose}><Loading /></ModalShell>;

  return (
    <ModalShell onClose={onClose}>
      <div style={{ fontSize: 11, color: theme.accentDark, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>
        {mode === 'create' ? 'Nueva orden de compra' : 'Editar PO'}
      </div>
      <h2 style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 400, color: theme.brand, margin: '4px 0 20px' }}>
        {poNumber}
      </h2>

      <form onSubmit={save}>
        {selectedSupplier && (
          <div style={{ marginBottom: 14, padding: '10px 14px', background: theme.bgSoft, border: `1px solid ${theme.border}`, fontSize: 12, color: theme.textMuted, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <span>Lead time: <strong style={{ color: theme.brand }}>{selectedSupplier.lead_time_days ?? '—'} días</strong></span>
            <span>MOQ: <strong style={{ color: theme.brand }}>{selectedSupplier.min_order_qty ?? '—'}</strong></span>
            <span>Mín. orden: <strong style={{ color: theme.brand }}>{selectedSupplier.min_order_value != null ? `$${Number(selectedSupplier.min_order_value).toLocaleString('en-US')} ${selectedSupplier.currency}` : '—'}</strong></span>
            {selectedSupplier.incoterms && <span>Incoterms: <strong style={{ color: theme.brand }}>{selectedSupplier.incoterms}</strong></span>}
            {selectedSupplier.payment_terms && <span>Términos: <strong style={{ color: theme.brand }}>{selectedSupplier.payment_terms}</strong></span>}
            {(((selectedSupplier.min_order_qty ?? 0) > 0 && totalQty < (selectedSupplier.min_order_qty ?? 0)) || ((selectedSupplier.min_order_value ?? 0) > 0 && subtotal < (selectedSupplier.min_order_value ?? 0))) && (
              <span style={{ color: '#B91C1C', fontWeight: 700 }}>⚠ No alcanza el mínimo del proveedor</span>
            )}
          </div>
        )}
        <PORow>
          <POField label="Proveedor *">
            <select value={supplierId} onChange={e => setSupplierId(e.target.value)} style={poInput} required>
              <option value="">— selecciona —</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.country} · {s.currency})</option>
              ))}
            </select>
          </POField>
          <POField label="Moneda">
            <select value={currency} onChange={e => setCurrency(e.target.value)} style={poInput}>
              <option value="USD">USD</option>
              <option value="MXN">MXN</option>
              <option value="CNY">CNY</option>
              <option value="EUR">EUR</option>
            </select>
          </POField>
          <POField label="Status">
            <select value={status} onChange={e => setStatus(e.target.value)} style={poInput}>
              <option value="draft">Borrador</option>
              <option value="sent">Enviada</option>
              <option value="confirmed">Confirmada</option>
              <option value="partial">Parcial</option>
              <option value="received">Recibida</option>
              <option value="cancelled">Cancelada</option>
            </select>
          </POField>
        </PORow>

        <PORow>
          <POField label="Fecha orden">
            <input type="date" value={orderedAt} onChange={e => setOrderedAt(e.target.value)} style={poInput} />
          </POField>
          <POField label="ETA esperada">
            <input type="date" value={expectedAt} onChange={e => setExpectedAt(e.target.value)} style={poInput} />
          </POField>
          <POField label="Factura proveedor">
            <input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} style={poInput} />
          </POField>
        </PORow>

        <POField label="Notas">
          <textarea value={notes} onChange={e => setNotes(e.target.value)} style={{ ...poInput, minHeight: 60, resize: 'vertical' }} />
        </POField>

        {/* Items */}
        <div style={{ marginTop: 20, marginBottom: 8, fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
          Items ({items.length})
        </div>

        <div style={{ position: 'relative', marginBottom: 12 }}>
          <input
            placeholder="Agregar SKU o producto…"
            value={skuQuery}
            onChange={e => setSkuQuery(e.target.value)}
            style={poInput}
          />
          {skuMatches.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: theme.bg, border: `1px solid ${theme.border}`, maxHeight: 240, overflowY: 'auto' }}>
              {skuMatches.map(m => (
                <button
                  type="button"
                  key={`${m.product_id}:${m.variant_id ?? 'base'}`}
                  onClick={() => addItem(m)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '8px 12px', border: 'none', borderBottom: `1px solid ${theme.border}`,
                    background: 'transparent', cursor: 'pointer', fontFamily: theme.font.sans,
                  }}
                >
                  <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: theme.font.mono }}>{m.sku}</div>
                  <div style={{ fontSize: 13, color: theme.brand }}>{m.name}</div>
                  {m.characteristics && <div style={{ fontSize: 10, color: theme.accentDark, marginTop: 2 }}>{m.characteristics}</div>}
                </button>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div style={{ border: `1px solid ${theme.border}`, marginBottom: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: theme.bgSoft }}>
                  <th style={th}>SKU</th>
                  <th style={th}>Descripción</th>
                  <th style={{ ...th, textAlign: 'right', width: 90 }}>Cant</th>
                  {mode === 'edit' && <th style={{ ...th, textAlign: 'right', width: 90 }}>Recibido</th>}
                  <th style={{ ...th, textAlign: 'right', width: 110 }}>Costo unit</th>
                  <th style={{ ...th, textAlign: 'right', width: 110 }}>Total</th>
                  <th style={{ ...th, width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={{ ...td, fontFamily: theme.font.mono, fontSize: 11 }}>{it.sku}</td>
                    <td style={td}>{it.description}{it.characteristics && <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 3, lineHeight: 1.4 }}>{it.characteristics}</div>}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <input type="number" value={it.quantity} onChange={e => updateItem(i, { quantity: Number(e.target.value) })} style={tinyInput} step="1" />
                    </td>
                    {mode === 'edit' && (
                      <td style={{ ...td, textAlign: 'right' }}>
                        <span style={{ color: it.qty_received! >= it.quantity ? '#15803D' : theme.textMuted }}>
                          {it.qty_received ?? 0}
                        </span>
                      </td>
                    )}
                    <td style={{ ...td, textAlign: 'right' }}>
                      <input type="number" step="0.01" value={it.unit_cost} onChange={e => updateItem(i, { unit_cost: Number(e.target.value) })} style={tinyInput} />
                      {it.product_id && refCosts[it.product_id] && (
                        <div style={{ fontSize: 9, marginTop: 2, textAlign: 'right', color: it.unit_cost > refCosts[it.product_id].cost * 1.05 ? '#B91C1C' : theme.textMuted }}>
                          {it.unit_cost > refCosts[it.product_id].cost * 1.05 ? '⚠ ' : ''}ref ${refCosts[it.product_id].cost.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>
                      ${(it.quantity * it.unit_cost).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <button type="button" onClick={() => removeItem(i)} style={{
                        background: 'transparent', border: 'none', color: theme.textMuted, cursor: 'pointer', fontSize: 14,
                      }}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <PORow>
          <POField label="Envío / flete">
            <input type="number" step="0.01" value={shipping} onChange={e => setShipping(Number(e.target.value))} style={poInput} />
          </POField>
          <POField label="Impuestos / aduana">
            <input type="number" step="0.01" value={tax} onChange={e => setTax(Number(e.target.value))} style={poInput} />
          </POField>
        </PORow>

        <div style={{ marginTop: 20, padding: 16, background: theme.bgFooter, border: `1px solid ${theme.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: theme.textMuted, marginBottom: 4 }}>
            <span>Subtotal</span>
            <span>${subtotal.toLocaleString('en-US', { maximumFractionDigits: 2 })} {currency}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: theme.textMuted, marginBottom: 4 }}>
            <span>Envío</span><span>${shipping.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: theme.textMuted, marginBottom: 8 }}>
            <span>Impuestos</span><span>${tax.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700, color: theme.brand, paddingTop: 8, borderTop: `1px solid ${theme.border}` }}>
            <span>Total</span>
            <span>${total.toLocaleString('en-US', { maximumFractionDigits: 2 })} {currency}</span>
          </div>
        </div>

        {err && (
          <div style={{ color: '#B91C1C', fontSize: 13, marginTop: 12, padding: 10, background: 'rgba(220,38,38,0.05)', border: '1px solid #B91C1C' }}>
            {err}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'space-between' }}>
          {mode === 'edit' && status !== 'received' && status !== 'cancelled' && (
            <button type="button" onClick={receiveAll} style={{
              background: theme.accent, color: theme.brand, border: 'none',
              padding: '10px 18px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 0.4, cursor: 'pointer', fontFamily: theme.font.sans,
            }}>
              📥 Recibir todo
            </button>
          )}
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button type="button" onClick={onClose} style={{
              background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand,
              padding: '10px 18px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 0.4, cursor: 'pointer', fontFamily: theme.font.sans,
            }}>Cancelar</button>
            <button type="submit" disabled={saving} style={{
              ...btnPrimary, opacity: saving ? 0.7 : 1, cursor: saving ? 'wait' : 'pointer',
            }}>
              {saving ? 'Guardando…' : mode === 'create' ? 'Crear PO' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </form>
    </ModalShell>
  );
}

// ============ Modal proveedor ============
function SupplierModal({ supplierId, onClose, onSaved }: { supplierId?: string; onClose: () => void; onSaved: () => void }) {
  const [s, setS] = useState({
    name: '', contact_name: '', email: '', phone: '', whatsapp: '',
    country: 'MX', currency: 'USD', lead_time_days: 30,
    payment_terms: '', brand_lines: '' as string,
    min_order_qty: '' as string, min_order_value: '' as string,
    order_multiple: '' as string, incoterms: '', import_factor_pct: '' as string,
    notes: '', active: true,
  });
  const [loading, setLoading] = useState(!!supplierId);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!supplierId) return;
    (async () => {
      const { data } = await supabase.from('suppliers').select('*').eq('id', supplierId).maybeSingle();
      const d = data as any;
      if (d) setS({
        name: d.name ?? '', contact_name: d.contact_name ?? '', email: d.email ?? '',
        phone: d.phone ?? '', whatsapp: d.whatsapp ?? '', country: d.country ?? 'MX',
        currency: d.currency ?? 'USD', lead_time_days: d.lead_time_days ?? 30,
        payment_terms: d.payment_terms ?? '', brand_lines: (d.brand_lines ?? []).join(', '),
        min_order_qty: d.min_order_qty != null ? String(d.min_order_qty) : '',
        min_order_value: d.min_order_value != null ? String(d.min_order_value) : '',
        order_multiple: d.order_multiple != null ? String(d.order_multiple) : '',
        incoterms: d.incoterms ?? '', import_factor_pct: d.import_factor_pct != null ? String(d.import_factor_pct) : '',
        notes: d.notes ?? '', active: d.active ?? true,
      });
      setLoading(false);
    })();
  }, [supplierId]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      const payload = {
        name: s.name, contact_name: s.contact_name || null, email: s.email || null,
        phone: s.phone || null, whatsapp: s.whatsapp || null, country: s.country,
        currency: s.currency, lead_time_days: s.lead_time_days,
        payment_terms: s.payment_terms || null,
        brand_lines: s.brand_lines.split(',').map(t => t.trim()).filter(Boolean),
        min_order_qty: s.min_order_qty ? Number(s.min_order_qty) : null,
        min_order_value: s.min_order_value ? Number(s.min_order_value) : null,
        order_multiple: s.order_multiple ? Number(s.order_multiple) : null,
        incoterms: s.incoterms || null,
        import_factor_pct: s.import_factor_pct ? Number(s.import_factor_pct) : 20,
        notes: s.notes || null, active: s.active,
      };
      const { error } = supplierId
        ? await supabase.from('suppliers').update(payload).eq('id', supplierId)
        : await supabase.from('suppliers').insert(payload);
      if (error) throw error;
      onSaved();
    } catch (e: any) {
      setErr(e.message ?? 'Error');
    } finally { setSaving(false); }
  }

  if (loading) return <ModalShell onClose={onClose}><Loading /></ModalShell>;

  return (
    <ModalShell onClose={onClose}>
      <div style={{ fontSize: 11, color: theme.accentDark, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>
        {supplierId ? 'Editar proveedor' : 'Nuevo proveedor'}
      </div>
      <h2 style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 400, color: theme.brand, margin: '4px 0 20px' }}>
        {supplierId ? s.name || 'Proveedor' : 'Alta de proveedor'}
      </h2>
      <form onSubmit={save}>
        <POField label="Nombre *">
          <input required value={s.name} onChange={e => setS({ ...s, name: e.target.value })} style={poInput} />
        </POField>
        <PORow>
          <POField label="Contacto"><input value={s.contact_name} onChange={e => setS({ ...s, contact_name: e.target.value })} style={poInput} /></POField>
          <POField label="Email"><input type="email" value={s.email} onChange={e => setS({ ...s, email: e.target.value })} style={poInput} /></POField>
        </PORow>
        <PORow>
          <POField label="Teléfono"><input value={s.phone} onChange={e => setS({ ...s, phone: e.target.value })} style={poInput} /></POField>
          <POField label="WhatsApp"><input value={s.whatsapp} onChange={e => setS({ ...s, whatsapp: e.target.value })} style={poInput} /></POField>
        </PORow>
        <PORow>
          <POField label="País">
            <select value={s.country} onChange={e => setS({ ...s, country: e.target.value })} style={poInput}>
              <option value="MX">México</option>
              <option value="US">Estados Unidos</option>
              <option value="CN">China</option>
              <option value="OTHER">Otro</option>
            </select>
          </POField>
          <POField label="Moneda">
            <select value={s.currency} onChange={e => setS({ ...s, currency: e.target.value })} style={poInput}>
              <option value="USD">USD</option><option value="MXN">MXN</option><option value="CNY">CNY</option><option value="EUR">EUR</option>
            </select>
          </POField>
          <POField label="Lead time días">
            <input type="number" value={s.lead_time_days} onChange={e => setS({ ...s, lead_time_days: Number(e.target.value) })} style={poInput} />
          </POField>
        </PORow>
        <PORow>
          <POField label="MOQ (piezas)">
            <input type="number" value={s.min_order_qty} onChange={e => setS({ ...s, min_order_qty: e.target.value })} style={poInput} placeholder="ej. 50" />
          </POField>
          <POField label="Mín. orden (monto)">
            <input type="number" value={s.min_order_value} onChange={e => setS({ ...s, min_order_value: e.target.value })} style={poInput} placeholder="ej. 500" />
          </POField>
          <POField label="Múltiplo empaque">
            <input type="number" value={s.order_multiple} onChange={e => setS({ ...s, order_multiple: e.target.value })} style={poInput} placeholder="ej. 1" />
          </POField>
        </PORow>
        <PORow>
          <POField label="Incoterms">
            <input value={s.incoterms} onChange={e => setS({ ...s, incoterms: e.target.value })} style={poInput} placeholder="EXW, FOB, DDP…" />
          </POField>
          <POField label="% Importación (impuestos/aduana)">
            <input type="number" step="0.1" value={s.import_factor_pct} onChange={e => setS({ ...s, import_factor_pct: e.target.value })} style={poInput} placeholder="20" />
          </POField>
          <POField label="Términos pago">
            <input value={s.payment_terms} onChange={e => setS({ ...s, payment_terms: e.target.value })} style={poInput} placeholder="50% anticipo, 50% antes envío" />
          </POField>
        </PORow>
        <POField label="Líneas / marcas (coma)">
          <input value={s.brand_lines} onChange={e => setS({ ...s, brand_lines: e.target.value })} style={poInput} placeholder="nuled, lutron" />
        </POField>
        <div style={{ fontSize: 11, color: theme.textMuted, marginTop: -8, marginBottom: 12 }}>
          Las líneas mapean qué marcas surte (ej. <strong>nuled</strong>, <strong>lutron</strong>). Así el sistema sabe qué proveedor corresponde a cada SKU.
        </div>
        <POField label="Notas">
          <textarea value={s.notes} onChange={e => setS({ ...s, notes: e.target.value })} style={{ ...poInput, minHeight: 60, resize: 'vertical' }} />
        </POField>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, color: theme.brand, cursor: 'pointer' }}>
          <input type="checkbox" checked={s.active} onChange={e => setS({ ...s, active: e.target.checked })} />
          Proveedor activo
        </label>

        {err && (
          <div style={{ color: '#B91C1C', fontSize: 13, marginTop: 12, padding: 10, background: 'rgba(220,38,38,0.05)', border: '1px solid #B91C1C' }}>{err}</div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{
            background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand,
            padding: '10px 18px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.4, cursor: 'pointer', fontFamily: theme.font.sans,
          }}>Cancelar</button>
          <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1, cursor: saving ? 'wait' : 'pointer' }}>
            {saving ? 'Guardando…' : (supplierId ? 'Guardar cambios' : 'Crear proveedor')}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============ Helpers ============
function ModalShell({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.bg, border: `1px solid ${theme.border}`,
        padding: 32, width: '100%', maxWidth: 880, maxHeight: '92vh', overflowY: 'auto',
        position: 'relative',
      }}><button type="button" onClick={onClose} aria-label="Cerrar" title="Cerrar" style={{ position: 'absolute', top: 10, right: 14, background: 'transparent', border: 'none', color: theme.textMuted, fontSize: 26, lineHeight: 1, cursor: 'pointer', padding: 4, zIndex: 5 }}>×</button>{children}</div>
    </div>
  );
}

function Loading() {
  return <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>Cargando…</div>;
}

function POField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <div style={{
        fontSize: 11, color: theme.textMuted, marginBottom: 6,
        textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
      }}>{label}</div>
      {children}
    </label>
  );
}

function PORow({ children }: { children: React.ReactNode }) {
  const cols = Array.isArray(children) ? children.length : 1;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>
      {children}
    </div>
  );
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th style={{
      textAlign: 'left', padding: '12px 14px',
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
      color: theme.textMuted, borderBottom: `1px solid ${theme.border}`,
      ...style,
    }}>{children}</th>
  );
}

function Td({ children, style, onClick }: { children: React.ReactNode; style?: React.CSSProperties; onClick?: (e: React.MouseEvent<HTMLTableCellElement>) => void }) {
  return <td onClick={onClick} style={{ padding: '10px 14px', verticalAlign: 'middle', ...style }}>{children}</td>;
}

function Tag({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', color,
      border: `1px solid ${color}`, textTransform: 'uppercase', letterSpacing: 0.5,
    }}>{children}</span>
  );
}

function SubBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: active ? theme.brand : 'transparent',
      color: active ? theme.textOnBrand : theme.brand,
      border: 'none', padding: '10px 16px', fontSize: 12, fontWeight: 700,
      letterSpacing: 0.4, textTransform: 'uppercase',
      cursor: 'pointer', fontFamily: theme.font.sans,
    }}>{children}</button>
  );
}

const poInput: React.CSSProperties = {
  width: '100%', background: theme.bg, border: `1px solid ${theme.border}`,
  padding: '10px 12px', color: theme.text, fontSize: 13,
  fontFamily: theme.font.sans, outline: 'none', boxSizing: 'border-box',
};

const tinyInput: React.CSSProperties = {
  width: '100%', background: theme.bg, border: `1px solid ${theme.border}`,
  padding: '4px 6px', fontSize: 12, fontFamily: theme.font.mono,
  textAlign: 'right', outline: 'none', boxSizing: 'border-box',
};

const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px',
  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
  color: theme.textMuted, borderBottom: `1px solid ${theme.border}`,
};
const td: React.CSSProperties = { padding: '6px 10px', verticalAlign: 'middle' };

const btnPrimary: React.CSSProperties = {
  background: theme.accent, color: theme.brand, border: 'none',
  padding: '10px 18px', fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
  textTransform: 'uppercase', cursor: 'pointer', fontFamily: theme.font.sans,
};
